import './LightroomPluginGuide.scss'

import { DownloadLink, useGuideLanguage } from './DownloadLink.component'
import { FC, Fragment, ReactNode } from 'react'
import { GuideImage, GuideItem, GuideSection, GuideStep, getLightroomGuideContent } from './content'

import { PageTransition } from 'utils/animations'
import { getPrivacyPolicyLink } from 'utils/localization/terms'

const INSTALL_SECTION_ID = 'install'

/**
 * Creative guide for the Backbone plugin for Lightroom Classic.
 * Port of the design system guide page: markup and class names match it, styles live in LightroomPluginGuide.scss.
 * @example <LightroomPluginGuidePage />
 */
export const LightroomPluginGuidePage: FC = () => {
  const language = useGuideLanguage()
  const content = getLightroomGuideContent(language)
  const { expect, install, daily, know, formats, help } = content

  return (
    <PageTransition>
      <div className="lr-guide" lang={content.lang}>
        <div className="wrap">

          <div className="hero">
            <div>
              <p className="eyebrow">{content.eyebrow}</p>
              <h1>{content.title}</h1>
              <p className="lead">{content.lead}</p>
              <div className="actions">
                <DownloadButton label={content.download} placement="hero" />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => document.getElementById(INSTALL_SECTION_ID)?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {content.howToInstall}
                </button>
              </div>
              <p className="meta">{content.meta}</p>
            </div>
            <Shot image={content.hero} />
          </div>

          <div className="benefits">
            {content.benefits.map(item => (
              <div key={item.title} className="benefit"><h3>{item.title}</h3><p>{item.body}</p></div>
            ))}
          </div>

          <Section section={expect}>
            <div className="field">
              {expect.items.map(item => (
                <div key={item.title} className="item"><h3>{item.title}</h3><p>{item.body}</p></div>
              ))}
            </div>
          </Section>

          <Section section={install} id={INSTALL_SECTION_ID}>
            <div className="req">
              {install.requirements.map(item => (
                <div key={item.title}>
                  <p className="eyebrow">{item.title}</p>
                  <p className={item.isNegative ? 'no' : undefined}>{item.body}</p>
                </div>
              ))}
            </div>
            <Steps steps={install.steps} />
          </Section>

          <Section section={daily}>
            <Steps steps={daily.steps} />
          </Section>

          <Section section={know}>
            <Facts items={know.items} />
            <Shot image={know.update} className="update-shot" />
            <Facts items={know.moreItems} />
          </Section>

          <Section section={formats}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>{formats.headers.map(header => <th key={header}>{header}</th>)}</tr>
                </thead>
                <tbody>
                  {formats.rows.map(row => (
                    <tr key={row.files}>
                      <td>{row.files}</td>
                      <td><span className={`pill ${row.kind}`}>{row.outcome}</span></td>
                      <td>{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section section={help}>
            <div className="faq">
              {help.faq.map(entry => (
                <details key={entry.question}>
                  <summary>{entry.question}</summary>
                  <div className="a"><p>{entry.answer}</p></div>
                </details>
              ))}
            </div>
          </Section>

          <div className="cta">
            <h2>{content.ready}</h2>
            <DownloadButton label={content.download} placement="cta" />
          </div>

          <footer className="foot">
            <span>Backbone Art SA · {content.product}</span>
            <span>
              {content.support} · <a href={getPrivacyPolicyLink(language.toLowerCase())} target="_blank" rel="noopener noreferrer">{content.privacy}</a>
            </span>
          </footer>

        </div>
      </div>
    </PageTransition>
  )
}

const DownloadButton: FC<{ label: string, placement: 'hero' | 'cta' }> = ({ label, placement }) => (
  <DownloadLink className="btn btn-primary" placement={placement}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12m0 0-5-5m5 5 5-5M5 21h14" />
    </svg>
    {label}
  </DownloadLink>
)

const Section: FC<{ section: GuideSection, id?: string, children: ReactNode }> = ({ section, id, children }) => (
  <section id={id}>
    <div className="sec-head">
      <p className="eyebrow">{section.eyebrow}</p>
      <h2>{section.title}</h2>
      {!!section.intro && <p className="intro">{section.intro}</p>}
    </div>
    <div className="body">
      {children}
    </div>
  </section>
)

const Steps: FC<{ steps: GuideStep[] }> = ({ steps }) => (
  <ol className="steps">
    {steps.map((step, index) => (
      <li key={step.title} className="step">
        <div className="n">{index + 1}</div>
        <div>
          <h3>{step.title}</h3>
          <p>{step.body}</p>
          {!!step.path &&
            <div className="path">
              {step.path.map((label, labelIndex) => (
                <Fragment key={label}>
                  {labelIndex > 0 && <i>›</i>}
                  <span>{label}</span>
                </Fragment>
              ))}
            </div>
          }
          {!!step.image && <Shot image={step.image} />}
          {!!step.note && <div className={step.note.isSuccess ? 'note ok' : 'note'}>{step.note.body}</div>}
        </div>
      </li>
    ))}
  </ol>
)

const Facts: FC<{ items: GuideItem[] }> = ({ items }) => (
  <div className="grid2">
    {items.map(item => (
      <div key={item.title} className="fact"><h3>{item.title}</h3><p>{item.body}</p></div>
    ))}
  </div>
)

const Shot: FC<{ image: GuideImage, className?: string }> = ({ image, className }) => (
  <figure className={className ? `shot ${className}` : 'shot'}>
    <img src={image.src} alt={image.alt} />
    <figcaption>{image.caption}</figcaption>
  </figure>
)
